import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import Modal from './Modal';
import constants from '../../const';

// ============================================================================
// Styled Components
// ============================================================================

const ModernButtonStyle = styled.button`
  border-radius: 6px;
  font-weight: 600;
  padding: 6px 12px;
  transition: all 0.3s ease;
  border: none;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  font-family: inherit;

  &.btn-primary {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;

    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 10px 20px rgba(102, 126, 234, 0.3);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  &.btn-light {
    background: #ffffff;
    color: #667eea;
    border: 2px solid #667eea;

    &:hover:not(:disabled) {
      background: #667eea;
      color: white;
      transform: translateY(-2px);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
`;

const ModernButton = (props) => {
  const { variant, onClick, disabled, children, style, className } = props;
  const btnClass = variant ? `btn-${variant}` : '';
  
  return (
    <ModernButtonStyle
      className={`${btnClass} ${className || ''}`}
      onClick={onClick}
      disabled={disabled}
      style={style}
      type="button"
    >
      {children}
    </ModernButtonStyle>
  );
};

const SearchInputStyle = styled.input`
  width: 100%;
  padding: 12px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }

  &::placeholder {
    color: #999;
  }
`;

const CategorySection = styled.div`
  margin-bottom: 12px;
`;

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  user-select: none;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  }

  .expand-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    transition: transform 0.3s ease;
    transform: ${props => props.expanded ? 'rotate(0deg)' : 'rotate(-90deg)'};
  }
`;

const PositionGridContent = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 6px;
  margin-top: 8px;
  pointer-events: auto;
`;

const PositionSelectCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 4px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid #e0e0e0;
  background: white;
  pointer-events: auto;

  &:hover {
    border-color: #667eea;
    transform: translateY(-4px);
    box-shadow: 0 8px 16px rgba(102, 126, 234, 0.2);
  }

  &.selected {
    border-color: #667eea;
    background: linear-gradient(135deg, #f0f4ff 0%, #e3e9ff 100%);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  }

  .position-image {
    width: 100%;
    aspect-ratio: 125 / 83;
    object-fit: contain;
    margin-bottom: 3px;
    border-radius: 4px;
    background-color: #f5f5f5;
    pointer-events: none;
  }

  .position-info {
    text-align: center;
    width: 100%;
    pointer-events: none;
    
    .position-name {
      font-size: 11px;
      font-weight: 600;
      color: #333;
      margin-bottom: 1px;
      word-break: break-word;
      line-height: 1.2;
    }

    .position-category {
      font-size: 9px;
      color: #999;
      line-height: 1.1;
    }
  }
`;

const EmptyStateMessage = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: #999;
  font-size: 14px;
`;

// ============================================================================
// Component
// ============================================================================

/**
 * Reusable Position Selector Modal Component
 * 
 * @param {Object} props
 * @param {boolean} props.show - Whether modal is visible
 * @param {Function} props.onClose - Callback when modal closes
 * @param {Function} props.onSelect - Callback when position is selected (receives position object)
 * @param {Array} props.positions - Array of position objects to display
 * @param {string} props.title - Modal title (default: "Select Position")
 * @param {string} props.buttonText - Button text (default: "Select Position")
 * @param {Object} props.selected - Currently selected position object
 * @param {string} props.searchPlaceholder - Search input placeholder
 * @param {boolean} props.groupByCategory - Whether to group positions by category (default: true)
 * @param {string} props.categoryField - Field name for category (default: "category")
 * @param {string} props.searchField - Field name to search in (default: "name")
 * @param {Function} props.renderImage - Custom function to render position image
 * @param {string} props.filterCategory - Filter positions by this category name (e.g., "1Main")
 * 
 * NOTE: Images are constructed automatically from position.id using the format:
 * https://cdn.66movies.com/download/sus-media/positions/{position.id}.png?token={token}
 * If position.image is already set, that takes precedence.
 */
function PositionSelector(props) {
  const {
    show = false,
    onClose = () => {},
    onSelect = () => {},
    positions = [],
    title = 'Select Position',
    buttonText = 'Select Position',
    selected = null,
    searchPlaceholder = '🔍 Search positions...',
    groupByCategory = true,
    categoryField = 'category',
    searchField = 'name',
    renderImage = null,
    filterCategory = null
  } = props;

  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});

  // Generate default image URL for positions
  const getDefaultImageUrl = (position) => {
    if (!position || !position.id) return null;
    const token = localStorage.getItem('token');
    const tokenUrl = token ? `?token=${token.slice(7)}` : '';
    return `${constants.CONTENT_DOMAIN}download/sus-media/positions/${position.id}.png${tokenUrl}`;
  };

  // Group positions by category and filter by search
  const groupedPositions = useMemo(() => {
    let filtered = positions;

    // Filter by category if specified
    if (filterCategory) {
      filtered = positions.filter(pos => {
        let posCategory = pos[categoryField];
        // Handle case where category is an object
        if (typeof posCategory === 'object' && posCategory !== null) {
          posCategory = posCategory.name || '';
        }
        return posCategory === filterCategory;
      });
    }

    // Filter by search query
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(pos => {
        const searchValue = pos[searchField] || '';
        return searchValue.toString().toLowerCase().includes(query);
      });
    }

    // Group by category if enabled
    if (!groupByCategory) {
      return { 'All': filtered };
    }

    const grouped = {};
    filtered.forEach(position => {
      let categoryName = position[categoryField] || 'Other';
      // Handle case where categoryField is an object with a name property
      if (typeof categoryName === 'object' && categoryName !== null) {
        categoryName = categoryName.name || 'Other';
      }
      categoryName = categoryName.toString();
      
      if (!grouped[categoryName]) {
        grouped[categoryName] = [];
      }
      grouped[categoryName].push(position);
    });

    // Sort positions within each category by order field
    Object.keys(grouped).forEach(categoryName => {
      grouped[categoryName].sort((a, b) => {
        const orderA = a.order || 0;
        const orderB = b.order || 0;
        return orderA - orderB;
      });
    });

    return grouped;
  }, [positions, searchQuery, groupByCategory, categoryField, searchField, filterCategory]);

  const toggleCategory = (categoryName) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryName]: !prev[categoryName]
    }));
  };

  const handleSelectPosition = (position) => {
    onSelect(position);
    setSearchQuery('');
  };

  const handleClose = () => {
    setSearchQuery('');
    setExpandedCategories({});
    onClose();
  };

  if (!show) {
    return null;
  }

  const hasResults = Object.values(groupedPositions).some(arr => arr.length > 0);

  return (
    <Modal
      title={title}
      onClose={handleClose}
      position="center"
      width="900px"
      zIndex={1060}
      footer={
        <div style={{ display: 'flex', gap: '8px', width: '100%', justifyContent: 'flex-end' }}>
          <ModernButton variant="light" onClick={handleClose}>
            <CloseIcon /> Close
          </ModernButton>
          <ModernButton 
            variant="primary" 
            onClick={() => {
              if (selected) {
                handleSelectPosition(selected);
              }
            }}
            disabled={!selected}
          >
            <AddIcon /> {buttonText}
          </ModernButton>
        </div>
      }
    >
      <div style={{ marginBottom: '12px' }}>
        <SearchInputStyle 
          type="text"
          placeholder={searchPlaceholder}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          autoFocus
        />
      </div>

      {!hasResults ? (
        <EmptyStateMessage>
          No positions found. Try adjusting your search.
        </EmptyStateMessage>
      ) : (
        Object.entries(groupedPositions).map(([category, categoryPositions]) => (
          categoryPositions.length > 0 && (
            <CategorySection key={`cat-${category}`}>
              {groupByCategory && (
                <CategoryHeader 
                  expanded={expandedCategories[category] !== false}
                  onClick={() => toggleCategory(category)}
                >
                  <span style={{ flex: 1 }}>{category}</span>
                  <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)' }}>
                    ({categoryPositions.length})
                  </span>
                  <div className="expand-icon">
                    <span>▼</span>
                  </div>
                </CategoryHeader>
              )}

              {(!groupByCategory || expandedCategories[category] !== false) && (
                <PositionGridContent>
                  {categoryPositions.map(position => (
                    <PositionSelectCard 
                      key={`pos-${position.id}`}
                      onClick={() => handleSelectPosition(position)}
                      className={selected?.id === position.id ? 'selected' : ''}
                    >
                      {renderImage ? (
                        renderImage(position)
                      ) : position.image ? (
                        <img 
                          src={position.image}
                          alt={position.name}
                          className="position-image"
                        />
                      ) : getDefaultImageUrl(position) ? (
                        <img 
                          src={getDefaultImageUrl(position)}
                          alt={position.name}
                          className="position-image"
                        />
                      ) : (
                        <div className="position-image" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ccc' }}>
                          No Image
                        </div>
                      )}
                      <div className="position-info">
                        <div className="position-name">{position.name}</div>
                        {position.category && (
                          <div className="position-category">
                            {typeof position[categoryField] === 'object' 
                              ? position[categoryField]?.name || 'Unknown'
                              : position[categoryField]}
                          </div>
                        )}
                      </div>
                    </PositionSelectCard>
                  ))}
                </PositionGridContent>
              )}
            </CategorySection>
          )
        ))
      )}
    </Modal>
  );
}

export default PositionSelector;
